import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Clock, Archive, LayoutTemplate, Trash2, ChevronRight, Edit2, X, Save } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return "Yesterday";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch { return ""; }
}

const TABS = [
  { id: "start", label: "Start New", icon: Plus },
  { id: "drafts", label: "Resume Draft", icon: Clock },
  { id: "history", label: "View History", icon: Archive },
  { id: "templates", label: "Templates", icon: LayoutTemplate },
];

export default function JobBriefingLandingPage({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("start");
  const [drafts, setDrafts] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Custom template state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [tplName, setTplName] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const [tplJobTask, setTplJobTask] = useState("");
  const [tplSections, setTplSections] = useState([]);

  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (tab === "drafts") loadBriefings("draft", setDrafts);
    if (tab === "history") loadBriefings("completed", setCompleted);
    if (tab === "templates") loadTemplates();
  }, [tab]);

  async function loadBriefings(status, setter) {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/job-briefing/history?status=${status}&limit=20`);
      setter(res.data.briefings || []);
    } catch { setter([]); }
    finally { setLoading(false); }
  }

  async function loadTemplates() {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/job-briefing/templates`);
      setTemplates(res.data.templates || []);
    } catch { setTemplates([]); }
    finally { setLoading(false); }
  }

  async function deleteBriefing(id) {
    try {
      await axios.delete(`${API}/api/job-briefing/briefing/${id}`);
      setDrafts(prev => prev.filter(b => b.briefing_id !== id));
      setCompleted(prev => prev.filter(b => b.briefing_id !== id));
    } catch { /* silent */ }
  }

  function handleStartNew() {
    navigate("/job-briefing/new");
  }

  function handleResumeDraft(briefing) {
    navigate("/job-briefing/new", { state: { resume: briefing } });
  }

  function handleUseTemplate(tpl) {
    navigate("/job-briefing/new", { state: { template: tpl } });
  }

  function handleViewBriefing(briefing) {
    navigate("/job-briefing/new", { state: { viewCompleted: briefing } });
  }

  function resetForm() {
    setTplName(""); setTplDesc(""); setTplJobTask("");
    setTplSections([]);
    setEditingTemplate(null);
    setShowCreateForm(false);
  }

  function openEditForm(tpl) {
    setEditingTemplate(tpl);
    setTplName(tpl.name);
    setTplDesc(tpl.description);
    setTplJobTask(tpl.prefill?.job_task || "");
    setTplSections(tpl.sections || []);
    setShowCreateForm(true);
  }

  async function handleSaveTemplate() {
    if (!tplName.trim()) return;
    const body = {
      name: tplName.trim(),
      description: tplDesc.trim(),
      category: "Custom",
      prefill: tplJobTask ? { job_task: tplJobTask } : {},
      sections: tplSections,
    };
    try {
      if (editingTemplate) {
        await axios.put(`${API}/api/job-briefing/templates/custom/${editingTemplate.template_id}`, body, { headers });
      } else {
        await axios.post(`${API}/api/job-briefing/templates/custom`, body, { headers });
      }
      resetForm();
      loadTemplates();
    } catch { /* silent */ }
  }

  async function handleDeleteTemplate(tplId) {
    try {
      await axios.delete(`${API}/api/job-briefing/templates/custom/${tplId}`, { headers });
      loadTemplates();
    } catch { /* silent */ }
  }

  function addSection() {
    setTplSections(prev => [...prev, { title: "", content: "" }]);
  }

  function updateSection(idx, field, value) {
    setTplSections(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  function removeSection(idx) {
    setTplSections(prev => prev.filter((_, i) => i !== idx));
  }

  return (
    <div style={styles.page} data-testid="job-briefing-landing">
      <style>{cssText}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerCenter}>
          <span className="jbl-badge">Safety Assistant</span>
          <h1 style={styles.h1}>Job Brief Builder</h1>
          <p style={styles.subtitle}>Build structured job briefings with hazards, PPE, and task planning.</p>
        </div>
      </header>

      {/* Tab bar */}
      <div className="jbl-tabs" data-testid="tab-bar">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={`jbl-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
            >
              <Icon size={16} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="jbl-content" data-testid="tab-content">

        {/* Start New */}
        {tab === "start" && (
          <div className="jbl-start-section">
            <div className="jbl-start-card" onClick={handleStartNew} data-testid="start-new-btn">
              <div className="jbl-start-icon">
                <Plus size={32} />
              </div>
              <h2>Start a New Job Brief</h2>
              <p>Walk through each step — job details, hazards, PPE, crew, and emergency plan.</p>
              <span className="jbl-start-action">Begin Briefing <ChevronRight size={16} /></span>
            </div>

            <div className="jbl-quick-templates">
              <p className="jbl-section-label">Or start from a template</p>
              <div className="jbl-quick-grid">
                {["Substation Maintenance", "Breaker Switching / Isolation", "Line Work / Overhead Maintenance", "Confined Space Entry"].map((name, i) => (
                  <button
                    key={i}
                    className="jbl-quick-tpl"
                    onClick={() => { setTab("templates"); }}
                    data-testid={`quick-tpl-${i}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Drafts */}
        {tab === "drafts" && (
          <div>
            {loading ? (
              <div className="jbl-empty">Loading drafts...</div>
            ) : drafts.length === 0 ? (
              <div className="jbl-empty" data-testid="no-drafts">
                <Clock size={32} style={{ opacity: 0.3 }} />
                <p>No drafts found.</p>
                <span>In-progress briefings will appear here when you save a draft.</span>
              </div>
            ) : (
              <div className="jbl-list">
                {drafts.map(b => (
                  <div key={b.briefing_id} className="jbl-list-item" data-testid={`draft-${b.briefing_id}`}>
                    <div className="jbl-list-info" onClick={() => handleResumeDraft(b)}>
                      <h3>{b.title}</h3>
                      <div className="jbl-list-meta">
                        <span className="jbl-progress-badge">{b.progress_percent}% complete</span>
                        <span>{formatTimeAgo(b.updated_at)}</span>
                      </div>
                    </div>
                    <div className="jbl-list-actions">
                      <button className="jbl-resume-btn" onClick={() => handleResumeDraft(b)} data-testid={`resume-${b.briefing_id}`}>Resume</button>
                      <button className="jbl-delete-btn" onClick={() => deleteBriefing(b.briefing_id)} data-testid={`delete-draft-${b.briefing_id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History */}
        {tab === "history" && (
          <div>
            {loading ? (
              <div className="jbl-empty">Loading history...</div>
            ) : completed.length === 0 ? (
              <div className="jbl-empty" data-testid="no-history">
                <Archive size={32} style={{ opacity: 0.3 }} />
                <p>No completed briefings yet.</p>
                <span>Finished briefings will be saved here automatically.</span>
              </div>
            ) : (
              <div className="jbl-list">
                {completed.map(b => (
                  <div key={b.briefing_id} className="jbl-list-item" data-testid={`history-${b.briefing_id}`}>
                    <div className="jbl-list-info" onClick={() => handleViewBriefing(b)}>
                      <h3>{b.title}</h3>
                      <div className="jbl-list-meta">
                        <span className="jbl-status-badge completed">Completed</span>
                        <span>{formatTimeAgo(b.created_at)}</span>
                      </div>
                    </div>
                    <div className="jbl-list-actions">
                      <button className="jbl-resume-btn" onClick={() => handleViewBriefing(b)} data-testid={`view-${b.briefing_id}`}>View</button>
                      <button className="jbl-delete-btn" onClick={() => deleteBriefing(b.briefing_id)} data-testid={`delete-history-${b.briefing_id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Templates */}
        {tab === "templates" && (
          <div>
            {/* Create/Edit form */}
            {showCreateForm ? (
              <div className="jbl-create-form" data-testid="template-form">
                <div className="jbl-form-header">
                  <h3>{editingTemplate ? "Edit Template" : "Create Custom Template"}</h3>
                  <button className="jbl-close-form" onClick={resetForm}><X size={16} /></button>
                </div>
                <input
                  className="jbl-form-input"
                  placeholder="Template Name"
                  value={tplName}
                  onChange={e => setTplName(e.target.value)}
                  data-testid="tpl-name-input"
                />
                <input
                  className="jbl-form-input"
                  placeholder="Description (optional)"
                  value={tplDesc}
                  onChange={e => setTplDesc(e.target.value)}
                  data-testid="tpl-desc-input"
                />
                <input
                  className="jbl-form-input"
                  placeholder="Default Job Task (optional pre-fill)"
                  value={tplJobTask}
                  onChange={e => setTplJobTask(e.target.value)}
                  data-testid="tpl-task-input"
                />
                <div className="jbl-form-sections">
                  <div className="jbl-form-sections-header">
                    <span>Sections</span>
                    <button className="jbl-add-section" onClick={addSection} data-testid="add-section-btn">
                      <Plus size={12} /> Add Section
                    </button>
                  </div>
                  {tplSections.map((s, i) => (
                    <div key={i} className="jbl-section-row" data-testid={`section-${i}`}>
                      <input
                        className="jbl-form-input"
                        placeholder="Section title"
                        value={s.title}
                        onChange={e => updateSection(i, "title", e.target.value)}
                      />
                      <textarea
                        className="jbl-form-textarea"
                        placeholder="Default content (optional)"
                        value={s.content}
                        onChange={e => updateSection(i, "content", e.target.value)}
                        rows={2}
                      />
                      <button className="jbl-remove-section" onClick={() => removeSection(i)}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="jbl-form-actions">
                  <button className="jbl-cancel-btn" onClick={resetForm}>Cancel</button>
                  <button
                    className="jbl-save-btn"
                    onClick={handleSaveTemplate}
                    disabled={!tplName.trim()}
                    data-testid="save-template-btn"
                  >
                    <Save size={14} /> {editingTemplate ? "Update" : "Save Template"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  className="jbl-create-tpl-btn"
                  onClick={() => setShowCreateForm(true)}
                  data-testid="create-template-btn"
                >
                  <Plus size={16} /> Create Custom Template
                </button>
                {loading ? (
                  <div className="jbl-empty">Loading templates...</div>
                ) : (
                  <div className="jbl-template-grid" data-testid="templates-grid">
                    {templates.map(tpl => (
                      <div
                        key={tpl.template_id}
                        className={`jbl-template-card ${tpl.category === "Custom" ? "custom" : ""}`}
                        data-testid={`template-${tpl.template_id}`}
                      >
                        <div className="jbl-tpl-top">
                          <span className="jbl-tpl-category">{tpl.category}</span>
                          {tpl.category === "Custom" && (
                            <div className="jbl-tpl-custom-actions">
                              <button className="jbl-tpl-edit" onClick={() => openEditForm(tpl)} data-testid={`edit-tpl-${tpl.template_id}`}><Edit2 size={12} /></button>
                              <button className="jbl-tpl-del" onClick={() => handleDeleteTemplate(tpl.template_id)} data-testid={`delete-tpl-${tpl.template_id}`}><Trash2 size={12} /></button>
                            </div>
                          )}
                        </div>
                        <h3>{tpl.name}</h3>
                        <p>{tpl.description}</p>
                        <button className="jbl-tpl-action" onClick={() => handleUseTemplate(tpl)}>
                          Use Template <ChevronRight size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 860,
    margin: "0 auto",
    padding: "20px 16px",
    minHeight: "100vh",
    fontFamily: "'Inter', -apple-system, sans-serif",
    background: "var(--surface-base)",
    color: "var(--text-primary)",
  },
  header: { marginBottom: 24 },
  headerCenter: { marginTop: 16 },
  h1: { fontSize: 28, fontWeight: 700, margin: "8px 0 4px" },
  subtitle: { color: "var(--text-secondary)", fontSize: 14, margin: 0 },
};

const cssText = `
.jbl-back-btn {
  background: none; border: 1px solid var(--border-default); color: var(--text-secondary);
  padding: 6px 14px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: 13px;
  transition: all 0.15s;
}
.jbl-back-btn:hover { border-color: var(--border-hover); color: var(--text-primary); }

.jbl-badge {
  display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600;
  background: var(--color-accent-soft); color: var(--color-accent); border: 1px solid var(--color-accent-border);
  text-transform: uppercase; letter-spacing: 0.04em;
}

/* Tabs */
.jbl-tabs {
  display: flex; gap: 6px; border-bottom: 1px solid var(--border-default); padding-bottom: 0; margin-bottom: 24px;
  overflow-x: auto; -webkit-overflow-scrolling: touch;
}
.jbl-tab {
  display: flex; align-items: center; gap: 6px; padding: 10px 16px; border: none; background: none;
  color: var(--text-muted); font-size: 13px; font-weight: 500; cursor: pointer; border-bottom: 2px solid transparent;
  white-space: nowrap; transition: all 0.15s;
}
.jbl-tab:hover { color: var(--text-secondary); }
.jbl-tab.active { color: var(--color-accent); border-bottom-color: var(--color-accent); }

/* Content */
.jbl-content { min-height: 300px; }

/* Start New */
.jbl-start-card {
  background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 16px;
  padding: 32px; cursor: pointer; transition: all 0.2s; text-align: center;
}
.jbl-start-card:hover { border-color: var(--color-accent-border); background: var(--surface-card-hover); transform: translateY(-2px); }
.jbl-start-icon {
  display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px;
  border-radius: 16px; background: var(--color-accent-soft); color: var(--color-accent);
  border: 1px solid var(--color-accent-border); margin-bottom: 16px;
}
.jbl-start-card h2 { font-size: 20px; font-weight: 700; margin: 0 0 8px; }
.jbl-start-card p { color: var(--text-secondary); font-size: 14px; margin: 0 0 16px; line-height: 1.5; }
.jbl-start-action {
  display: inline-flex; align-items: center; gap: 4px; font-size: 14px; font-weight: 600; color: var(--color-accent);
}

.jbl-quick-templates { margin-top: 28px; }
.jbl-section-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted); margin-bottom: 12px; }
.jbl-quick-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.jbl-quick-tpl {
  background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 20px;
  padding: 8px 16px; color: var(--text-secondary); font-size: 13px; cursor: pointer; transition: all 0.15s;
}
.jbl-quick-tpl:hover { border-color: var(--border-hover); color: var(--text-primary); }

/* List (Drafts / History) */
.jbl-list { display: flex; flex-direction: column; gap: 8px; }
.jbl-list-item {
  display: flex; justify-content: space-between; align-items: center;
  background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 12px;
  padding: 14px 16px; transition: all 0.15s;
}
.jbl-list-item:hover { border-color: var(--border-hover); }
.jbl-list-info { flex: 1; cursor: pointer; min-width: 0; }
.jbl-list-info h3 { font-size: 14px; font-weight: 600; margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.jbl-list-meta { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--text-muted); }
.jbl-progress-badge {
  padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600;
  background: var(--color-warning-soft); color: var(--color-warning); border: 1px solid rgba(245,158,11,0.2);
}
.jbl-status-badge.completed {
  padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600;
  background: var(--color-success-soft); color: var(--color-success); border: 1px solid rgba(34,197,94,0.2);
}
.jbl-list-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; margin-left: 12px; }
.jbl-resume-btn {
  background: var(--color-accent-soft); border: 1px solid var(--color-accent-border); color: var(--color-accent);
  padding: 6px 14px; border-radius: 8px; cursor: pointer; font-size: 12px; font-weight: 600; transition: all 0.15s;
}
.jbl-resume-btn:hover { background: var(--color-accent); color: var(--text-inverse); }
.jbl-delete-btn {
  background: none; border: 1px solid var(--border-default); color: var(--text-muted);
  padding: 6px 8px; border-radius: 8px; cursor: pointer; transition: all 0.15s;
}
.jbl-delete-btn:hover { border-color: var(--color-danger); color: var(--color-danger); }

/* Empty state */
.jbl-empty {
  text-align: center; padding: 48px 16px; color: var(--text-muted); font-size: 14px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.jbl-empty p { font-weight: 600; margin: 0; color: var(--text-secondary); }
.jbl-empty span { font-size: 13px; }

/* Templates */
.jbl-template-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px;
}
.jbl-template-card {
  background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px;
  padding: 20px; text-align: left; cursor: pointer; transition: all 0.2s;
}
.jbl-template-card:hover { border-color: var(--color-accent-border); background: var(--surface-card-hover); transform: translateY(-1px); }
.jbl-tpl-category {
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600;
  background: var(--color-accent-soft); color: var(--color-accent); border: 1px solid var(--color-accent-border);
  text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 10px;
}
.jbl-template-card h3 { font-size: 15px; font-weight: 700; margin: 0 0 6px; }
.jbl-template-card p { color: var(--text-secondary); font-size: 13px; line-height: 1.5; margin: 0 0 12px; }
.jbl-tpl-action {
  display: inline-flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 600; color: var(--color-accent);
}

/* Mobile */
@media (max-width: 640px) {
  .jbl-tabs { gap: 2px; }
  .jbl-tab { padding: 8px 12px; font-size: 12px; }
  .jbl-template-grid { grid-template-columns: 1fr; }
  .jbl-start-card { padding: 24px 16px; }
  .jbl-list-item { flex-direction: column; align-items: flex-start; gap: 10px; }
  .jbl-list-actions { margin-left: 0; width: 100%; }
  .jbl-resume-btn { flex: 1; text-align: center; }
}

/* Create template button */
.jbl-create-tpl-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; margin-bottom: 16px;
  border: 1px dashed rgba(168,85,247,0.4);
  background: rgba(168,85,247,0.05); color: #c084fc;
  transition: all 0.15s; width: 100%;
  justify-content: center;
}
.jbl-create-tpl-btn:hover { background: rgba(168,85,247,0.1); border-color: rgba(168,85,247,0.6); }

/* Custom template indicator */
.jbl-template-card.custom { border-color: rgba(168,85,247,0.25); }
.jbl-template-card.custom .jbl-tpl-category { background: rgba(168,85,247,0.1); color: #c084fc; border-color: rgba(168,85,247,0.2); }
.jbl-tpl-top { display: flex; justify-content: space-between; align-items: center; }
.jbl-tpl-custom-actions { display: flex; gap: 4px; }
.jbl-tpl-edit, .jbl-tpl-del {
  background: none; border: 1px solid rgba(255,255,255,0.06); color: #a1a1aa;
  padding: 4px 6px; border-radius: 4px; cursor: pointer; display: flex;
  transition: all 0.15s;
}
.jbl-tpl-edit:hover { color: #67e8f9; border-color: rgba(6,182,212,0.3); }
.jbl-tpl-del:hover { color: #f87171; border-color: rgba(239,68,68,0.3); }

/* Create/edit form */
.jbl-create-form {
  background: var(--surface-card); border: 1px solid var(--border-default);
  border-radius: 14px; padding: 20px;
}
.jbl-form-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;
}
.jbl-form-header h3 { font-size: 16px; font-weight: 600; margin: 0; }
.jbl-close-form {
  background: none; border: none; color: #71717a; cursor: pointer;
  padding: 4px; border-radius: 4px;
}
.jbl-close-form:hover { color: #e4e4e7; }
.jbl-form-input {
  width: 100%; padding: 10px 12px; border-radius: 8px; font-size: 13px;
  border: 1px solid var(--border-default); background: var(--surface-input);
  color: var(--text-primary); outline: none; margin-bottom: 10px;
  box-sizing: border-box; font-family: inherit;
}
.jbl-form-input:focus { border-color: var(--color-accent); }
.jbl-form-textarea {
  width: 100%; padding: 8px 12px; border-radius: 8px; font-size: 12px;
  border: 1px solid var(--border-default); background: var(--surface-input);
  color: var(--text-primary); outline: none; resize: vertical; font-family: inherit;
  box-sizing: border-box;
}
.jbl-form-textarea:focus { border-color: var(--color-accent); }
.jbl-form-sections { margin-top: 8px; }
.jbl-form-sections-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 10px; font-size: 12px; font-weight: 600; color: #a1a1aa;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.jbl-add-section {
  display: flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600;
  cursor: pointer; border: 1px solid var(--border-default);
  background: none; color: var(--text-secondary); transition: all 0.15s;
}
.jbl-add-section:hover { border-color: var(--border-hover); color: var(--text-primary); }
.jbl-section-row {
  position: relative; padding: 10px; border: 1px solid rgba(255,255,255,0.04);
  border-radius: 8px; margin-bottom: 8px; background: rgba(24,24,27,0.3);
}
.jbl-section-row .jbl-form-input { margin-bottom: 6px; }
.jbl-remove-section {
  position: absolute; top: 8px; right: 8px;
  background: none; border: none; color: #71717a; cursor: pointer; padding: 2px;
}
.jbl-remove-section:hover { color: #f87171; }
.jbl-form-actions {
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px;
}
.jbl-cancel-btn {
  padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500;
  border: 1px solid var(--border-default); background: none;
  color: var(--text-secondary); cursor: pointer;
}
.jbl-cancel-btn:hover { border-color: var(--border-hover); color: var(--text-primary); }
.jbl-save-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
  border: none; background: var(--color-accent); color: var(--text-inverse);
  cursor: pointer; transition: all 0.15s;
}
.jbl-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.jbl-save-btn:hover:not(:disabled) { opacity: 0.9; }
`;
